import React, { useMemo, useState } from "react";
import type { Bridge, BridgeMatrix } from "../types";
import { wasmBridgeProofSummary } from "../../wasm-bridge-proof-summary";
import {
  DemoWorkbenchCard,
  DemoWorkbenchCheckbox,
  DemoWorkbenchCopy,
  DemoWorkbenchInset,
  DemoWorkbenchPill,
  DemoWorkbenchStack,
  DemoWorkbenchTable,
} from "@csk/demo-ui";
import {
  buildWhatIfSimulation,
  footprintRows,
  matrixRuleKey,
} from "./what-if-simulator/whatIfSimulationModel.js";

export function WhatIfSimulator(props: {
  bridges: Bridge[] | null;
  matrix: BridgeMatrix | null;
  missingBridge: Bridge | null;
}): React.JSX.Element {
  const [toggleMissing, setToggleMissing] = useState(false);
  const [edgeToggles, setEdgeToggles] = useState<Partial<Record<string, boolean>>>({});
  const propsBridges = props.bridges;
  const propsMatrix = props.matrix;
  const propsMissingBridge = props.missingBridge;

  const sim = useMemo(() => {
    if (!propsBridges || !propsMatrix) return null;

    return buildWhatIfSimulation({
      bridges: propsBridges,
      matrix: propsMatrix,
      missingBridge: propsMissingBridge,
      toggleMissing,
      edgeToggles,
    });
  }, [
    propsBridges,
    propsMatrix,
    propsMissingBridge,
    toggleMissing,
    edgeToggles,
  ]);

  if (!props.bridges || !props.matrix) {
    return (
      <DemoWorkbenchCard title="What-if simulator">
        Load bridges and matrix first.
      </DemoWorkbenchCard>
    );
  }

  const topEdges = props.matrix.rules.slice(0, 12);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <DemoWorkbenchCard
        title="Δ experiments"
        right={<DemoWorkbenchPill>structural</DemoWorkbenchPill>}
      >
        <DemoWorkbenchCheckbox
          checked={toggleMissing}
          label="Admit missing bridge (δ move)"
          description="Simulates changing δ by adding a generator; nothing executes."
          onChange={setToggleMissing}
        />

        <div className="mt-4 text-sm text-slate-300">
          <div className="font-semibold text-slate-100">
            Edge flips (matrix what-if)
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Toggle a few edges to see path reachability change.
          </div>
          <DemoWorkbenchStack className="mt-2" gap={8}>
            {topEdges.map((r, i) => {
              const k = matrixRuleKey(r);
              const checked = edgeToggles[k];
              return (
                <DemoWorkbenchCheckbox
                  key={i}
                  checked={checked === true}
                  label={`flip ${r.lhs} → ${r.rhs} (${r.status})`}
                  className="text-xs"
                  onChange={(nextChecked) => {
                    setEdgeToggles({ ...edgeToggles, [k]: nextChecked });
                  }}
                />
              );
            })}
          </DemoWorkbenchStack>
        </div>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard
        title="Sim results"
        right={<DemoWorkbenchPill>{sim ? "ready" : "—"}</DemoWorkbenchPill>}
      >
        {!sim ? (
          <DemoWorkbenchCopy>Sim not ready.</DemoWorkbenchCopy>
        ) : (
          <DemoWorkbenchStack gap={12}>
            <DemoWorkbenchCopy>
              Sim bridges count:{" "}
              <span className="text-slate-100">{sim.simulatedBridgeCount}</span>
              <span className="text-slate-400">
                {" "}
                (base {props.bridges.length})
              </span>
            </DemoWorkbenchCopy>
            <DemoWorkbenchCopy>
              Changed rules:{" "}
              <span className="text-slate-100">{sim.changedRules}</span>
            </DemoWorkbenchCopy>
            {sim.certification ? (
              <DemoWorkbenchCopy size="xs" tone="muted">
                TS-WASM {sim.certification.theoremName}:{" "}
                {sim.certification.accepted ? "accepted" : "rejected"} ·{" "}
                {sim.certification.backend} · {sim.certification.fidelity}
                <br />
                seam {sim.certification.seamBridge.seamOrientation} · witness{" "}
                {sim.certification.seamBridge.requiredWitnessKind} ·{" "}
                {sim.certification.seamBridge.forbiddenGenerator} refused
                <br />
                {wasmBridgeProofSummary(sim.certification)}
              </DemoWorkbenchCopy>
            ) : null}

            <DemoWorkbenchInset title="Reachability probe">
              <DemoWorkbenchCopy size="xs" tone="muted" className="mt-1">
                Allowed path from CLASSIFY to HTTP_GET?
              </DemoWorkbenchCopy>
              {!sim.path ? (
                <DemoWorkbenchCopy className="mt-2">
                  No allowed path (unrepresentable under this matrix).
                </DemoWorkbenchCopy>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  {sim.path.map((p, i, arr) => (
                    <React.Fragment key={p}>
                      <DemoWorkbenchPill>{p}</DemoWorkbenchPill>
                      {i < arr.length - 1 ? (
                        <span className="text-slate-500">→</span>
                      ) : null}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </DemoWorkbenchInset>

            <DemoWorkbenchInset title="Footprint">
              <DemoWorkbenchTable
                headers={["axis", "base", "sim", "Δ"]}
                rows={footprintRows(sim)}
              />
            </DemoWorkbenchInset>
          </DemoWorkbenchStack>
        )}
      </DemoWorkbenchCard>
    </div>
  );
}
